<template>
  <div class="column content-box">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-top mt-50 bg-primary"
      icon="add-circle"
      @click="openAddPopup"
    >
      {{ $t('LABEL_MGMT.HEADER_BTN_TXT') }}
    </woot-button>

    <div v-if="showCrmTab" class="labels-tabs">
      <woot-tabs :index="selectedTabIndex" @change="onTabChange">
        <woot-tabs-item
          v-for="tab in tabs"
          :key="tab.key"
          :name="tab.name"
          :show-badge="false"
        />
      </woot-tabs>
    </div>

    <div class="row">
      <div class="small-8 columns with-right-space">
        <p
          v-if="!uiFlags.isFetching && !records.length"
          class="no-items-error-message"
        >
          {{ $t('LABEL_MGMT.LIST.404') }}
        </p>
        <woot-loading-state
          v-if="uiFlags.isFetching"
          :message="$t('LABEL_MGMT.LOADING')"
        />
        <table v-if="!uiFlags.isFetching && records.length" class="woot-table">
          <thead>
            <th v-for="thHeader in tableHeaders" :key="thHeader">
              {{ thHeader }}
            </th>
          </thead>
          <tbody>
            <tr v-for="label in records" :key="label.title">
              <td class="label-title">
                <span class="text-truncate">{{ label.title }}</span>
              </td>
              <td>{{ label.description }}</td>
              <td>
                <div class="label-color--container">
                  <span
                    class="label-color--display"
                    :style="{ backgroundColor: label.color }"
                  />
                  {{ label.color }}
                </div>
              </td>
              <td v-if="!isCrmTab">{{ labelScopeText(label) }}</td>
              <td class="button-wrapper">
                <woot-button
                  v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  class-names="grey-btn"
                  :is-loading="loading[label.id]"
                  icon="edit"
                  @click="openEditPopup(label)"
                />
                <woot-button
                  v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                  variant="smooth"
                  color-scheme="alert"
                  size="tiny"
                  icon="dismiss-circle"
                  class-names="grey-btn"
                  :is-loading="loading[label.id]"
                  @click="openDeletePopup(label)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="small-4 columns">
        <span v-dompurify-html="sidebarText" />
      </div>
    </div>

    <woot-modal :show.sync="showAddPopup" :on-close="hideAddPopup">
      <add-crm-label v-if="isCrmTab" @close="hideAddPopup" />
      <add-label v-else @close="hideAddPopup" />
    </woot-modal>

    <woot-modal :show.sync="showEditPopup" :on-close="hideEditPopup">
      <edit-crm-label
        v-if="isCrmTab"
        :selected-response="selectedResponse"
        @close="hideEditPopup"
      />
      <edit-label
        v-else
        :selected-response="selectedResponse"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { LABEL_VISIBILITY } from 'dashboard/helper/labelVisibility';

import AddLabel from './AddLabel';
import EditLabel from './EditLabel';
import AddCrmLabel from './AddCrmLabel';
import EditCrmLabel from './EditCrmLabel';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {
    AddLabel,
    EditLabel,
    AddCrmLabel,
    EditCrmLabel,
  },
  mixins: [alertMixin],
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      selectedTabIndex: 0,
    };
  },
  computed: {
    ...mapGetters({
      accountLabels: 'labels/getLabels',
      accountLabelsUiFlags: 'labels/getUIFlags',
      crmLabels: 'labelsCrm/getLabels',
      crmLabelsUiFlags: 'labelsCrm/getUIFlags',
      currentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
      getTeam: 'teams/getTeam',
      getInbox: 'inboxes/getInbox',
    }),
    tableHeaders() {
      const headers = this.$t('LABEL_MGMT.LIST.TABLE_HEADER');
      if (this.isCrmTab) return headers;
      return [...headers, this.$t('LABEL_MGMT.LIST.SCOPE_HEADER')];
    },
    account() {
      return this.getAccount(this.currentAccountId);
    },
    showCrmTab() {
      return !!this.account?.enable_crm;
    },
    isCrmTab() {
      return this.showCrmTab && this.selectedTabIndex === 1;
    },
    tabs() {
      return [
        {
          key: 'contact_conversation',
          name: this.$t('LABEL_MGMT.TABS.CONTACT_CONVERSATION'),
        },
        {
          key: 'crm',
          name: this.$t('LABEL_MGMT.TABS.CRM'),
        },
      ];
    },
    records() {
      return this.isCrmTab ? this.crmLabels : this.accountLabels;
    },
    uiFlags() {
      return this.isCrmTab ? this.crmLabelsUiFlags : this.accountLabelsUiFlags;
    },
    sidebarText() {
      return this.isCrmTab
        ? this.$t('LABEL_MGMT.SIDEBAR_CRM_TXT')
        : this.$t('LABEL_MGMT.SIDEBAR_TXT');
    },
    deleteConfirmText() {
      return `${this.$t('LABEL_MGMT.DELETE.CONFIRM.YES')} ${
        this.selectedResponse.title
      }`;
    },
    deleteRejectText() {
      return `${this.$t('LABEL_MGMT.DELETE.CONFIRM.NO')} ${
        this.selectedResponse.title
      }`;
    },
    deleteMessage() {
      return ` ${this.selectedResponse.title}?`;
    },
  },
  async mounted() {
    this.$store.dispatch('labels/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('inboxes/get');
    await this.$store.dispatch('accounts/get');
    if (this.showCrmTab) {
      this.$store.dispatch('labelsCrm/get');
    }
  },
  methods: {
    labelScopeText(label) {
      const options = 'LABEL_MGMT.FORM.VISIBILITY.OPTIONS';
      if (label.visibility === LABEL_VISIBILITY.TEAM) {
        const team = this.getTeam(label.team_id);
        return `${this.$t(`${options}.TEAM`)}: ${team.name || ''}`;
      }
      if (label.visibility === LABEL_VISIBILITY.INBOX) {
        const inbox = this.getInbox(label.inbox_id);
        return `${this.$t(`${options}.INBOX`)}: ${inbox.name || ''}`;
      }
      return this.$t(`${options}.SYSTEM`);
    },
    onTabChange(index) {
      this.selectedTabIndex = index;
      if (this.showCrmTab && index === 1) {
        this.$store.dispatch('labelsCrm/get');
      }
    },
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openEditPopup(response) {
      this.showEditPopup = true;
      this.selectedResponse = response;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.loading[this.selectedResponse.id] = true;
      this.closeDeletePopup();
      this.deleteLabel(this.selectedResponse.id);
    },
    deleteLabel(id) {
      const action = this.isCrmTab ? 'labelsCrm/delete' : 'labels/delete';
      this.$store
        .dispatch(action, id)
        .then(() => {
          this.showAlert(this.$t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
        })
        .catch(() => {
          this.showAlert(this.$t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE'));
        })
        .finally(() => {
          this.loading[this.selectedResponse.id] = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/variables';

.labels-tabs {
  margin-bottom: var(--space-normal);
}

.label-color--container {
  display: flex;
  align-items: center;
}

.label-color--display {
  border-radius: $space-smaller;
  height: $space-normal;
  margin-right: $space-smaller;
  width: $space-normal;
  border: 1px solid var(--color-border-light);
}
.label-title {
  span {
    width: var(--space-giga);
    display: inline-block;
  }
}
</style>
